/**
 * Settlement option helpers - derived from ``useSettlementOptions``.
 *
 * Phase 7 + PR #392 assimilation: every settlement dropdown vocabulary
 * lives on the backend as ``EntityAttributeDefinition.configuration_data.
 * options[*]`` on the Tier 2 ``settlement_line_item`` /
 * ``claim_payment`` entities. Per-option keys (``kind``, ``category``,
 * ``ledger_intent``, ``badge_variant``, ``allowed_items``) replace the
 * hardcoded constants the legacy ``settlement-constants.ts`` shipped.
 *
 * This module exposes plain helper functions that take a
 * ``SettlementOptions`` payload and derive the same shapes the legacy
 * constants exposed (badge maps, kind sets, default cascades, payment
 * validation matrix). The frontend never re-spells these as Set/Record
 * literals - every value travels with the option metadata.
 */

import type {
  SettlementOptions,
  SettlementOptionItem,
} from '@/admin/hooks/use-settlement'

type LegacyCategory = 'MATERIAL' | 'LABOR' | 'OTHER'

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive'

/** Empty/no-op options when the backend hasn't loaded yet. */
const EMPTY: SettlementOptionItem[] = []

/** Find an option by value. */
function find(
  options: SettlementOptionItem[] | undefined,
  value: string | null | undefined,
): SettlementOptionItem | undefined {
  if (!value || !options) return undefined
  return options.find((o) => o.value === value)
}

/**
 * The options an RS may still PICK - i.e. everything not marked
 * ``deprecated`` in the option payload.
 *
 * A retired option stays in the payload so a historical row keeps rendering
 * its real label (WPS-147 retired the "Goodwill" / "Sales Goodwill" item
 * details, which are ledgers rather than reasons, and the single legacy
 * "Goodwill" ledger). Pickers call this; label lookups keep using the full
 * list.
 */
export function selectableOptions(
  options: SettlementOptionItem[] | undefined,
): SettlementOptionItem[] {
  return (options ?? EMPTY).filter((option) => option.deprecated !== true)
}

/**
 * Resolve the legacy 3-way category for a line item.
 * Prefers item_detail (new model) → category (legacy column).
 */
export function resolveCategory(
  item: { item_detail?: string | null; category?: string },
  options?: SettlementOptions,
): LegacyCategory {
  const itemDetailOpt = find(options?.item_details, item.item_detail)
  const cat = itemDetailOpt?.category as LegacyCategory | undefined
  if (cat === 'MATERIAL' || cat === 'LABOR' || cat === 'OTHER') return cat
  if (
    item.category === 'MATERIAL' ||
    item.category === 'LABOR' ||
    item.category === 'OTHER'
  )
    return item.category
  return 'OTHER'
}

/**
 * Reverse-map a legacy category → default item_detail value.
 * Used when the user picks the legacy category dropdown on a tenant
 * that hasn't authored the 21-value item_detail set yet.
 */
export function legacyCategoryToItemDetail(
  category: string,
  options?: SettlementOptions,
): string {
  const items = options?.item_details ?? EMPTY
  // First item whose `category` metadata key matches.
  return items.find((o) => o.category === category)?.value ?? ''
}

/**
 * Look up the per-item-detail default ``ledger_type`` (WARRANTY|GOODWILL).
 * Drives the auto-fill cascade in the line-item form so picking
 * "GOODWILL" item flips the ledger choice without code.
 */
export function defaultLedgerForItem(
  itemDetail: string,
  options?: SettlementOptions,
): string {
  const opt = find(options?.item_details, itemDetail)
  const intent = (opt?.ledger_intent as string | undefined) ?? ''
  if (!intent) return ''
  // WPS-164 #18: the item's intended ledger may be filtered out for this claim
  // type (uninstalled shows Goodwill ledgers only, so an item whose intent is
  // WARRANTY - e.g. "Other" - has no selectable match and the field rendered
  // empty). When the intent is not among the ledgers this item may pick, fall
  // back to the first one so the dropdown always lands on a valid value. A
  // ledger the claim type excludes arrives ``deprecated`` and is never one of
  // them, so a default can never land on it.
  if (!options?.ledger_types?.length) return intent
  const available = getAllowedLedgerTypes(itemDetail, options)
  if (available.some((l) => l.value === intent)) return intent
  return available[0]?.value ?? ''
}

/**
 * The ledger types an RS may pick for a given item_detail, driven by the
 * per-option ``allowed_ledgers`` metadata (WPS-147 §7).
 *
 * Labour is coded as Warranty, never goodwill - the labor-variance LOA exists
 * precisely so an over-book labour adjustment escalates to a Team Leader
 * instead of being absorbed into goodwill. A LABOR item_detail therefore
 * carries ``allowed_ledgers: ["WARRANTY"]`` and this filters the ledger picker
 * to it. An item_detail with no ``allowed_ledgers`` (e.g. a Permit, which the
 * §3 worked example books to RS Goodwill) offers every selectable ledger, so
 * non-labour lines are unaffected.
 */
export function getAllowedLedgerTypes(
  itemDetail: string,
  options?: SettlementOptions,
): SettlementOptionItem[] {
  const ledgers = selectableOptions(options?.ledger_types)
  const opt = find(options?.item_details, itemDetail)
  const allowed = opt?.allowed_ledgers as string[] | undefined
  if (!Array.isArray(allowed) || allowed.length === 0) return ledgers
  const filtered = ledgers.filter((l) => allowed.includes(l.value))
  // Never strand the picker: if the allowlist matches nothing in the payload
  // (mis-seeded), fall back to the full selectable list rather than an empty
  // dropdown.
  return filtered.length > 0 ? filtered : ledgers
}

/** Set of item_detail values whose ``kind`` is MATERIAL - drives field visibility. */
export function materialItemDetails(options?: SettlementOptions): Set<string> {
  return new Set(
    (options?.item_details ?? EMPTY)
      .filter((o) => o.kind === 'MATERIAL')
      .map((o) => o.value),
  )
}

/** Set of item_detail values whose ``kind`` is LABOR. */
export function laborItemDetails(options?: SettlementOptions): Set<string> {
  return new Set(
    (options?.item_details ?? EMPTY)
      .filter((o) => o.kind === 'LABOR')
      .map((o) => o.value),
  )
}

/**
 * WPS-162: set of item_detail values whose ``product_source`` is
 * ``material_pricing`` - i.e. the item selects its product base from, and
 * prices against, the Material Pricing Table for the claim's geozone, never the
 * product library. Data-driven off the option metadata (no tenant slug); the
 * settlement dialog reads this to swap the product cascade for a single Product
 * Base picker fed by ``material_products``.
 */
export function materialPricingItemDetails(
  options?: SettlementOptions,
): Set<string> {
  return new Set(
    (options?.item_details ?? EMPTY)
      .filter((o) => o.product_source === 'material_pricing')
      .map((o) => o.value),
  )
}

/**
 * Work-type options for the labour card, with the unpriced ones named.
 *
 * WPS-165 #10: a labour line is meant to price itself from the approved
 * geozone book, so a work type the book does not cover cannot fill Unit Price.
 * The API marks those with ``has_published_rate: false`` (absent when no claim
 * was supplied, or on an older API - nothing is marked then).
 *
 * They stay SELECTABLE. The book may simply not be loaded yet, and refusing
 * the choice would block a legitimate line the operator holds a rate for. The
 * label just stops an empty Unit Price reading as a broken field.
 */
export function workTypeSelectOptions(
  options: SettlementOptions | undefined,
  labelUnpriced: (label: string) => string,
): { value: string; label: string }[] {
  return (options?.work_types ?? EMPTY).map((o) => ({
    value: o.value,
    label: o.has_published_rate === false ? labelUnpriced(o.label) : o.label,
  }))
}

/** Badge variant for an item_detail value, from option metadata. */
export function itemDetailBadgeVariant(
  itemDetail: string,
  options?: SettlementOptions,
): BadgeVariant {
  const opt = find(options?.item_details, itemDetail)
  return (opt?.badge_variant as BadgeVariant | undefined) ?? 'default'
}

/**
 * Semantic pill for a ledger: the tenant's primary ledger (badge variant
 * ``default``, e.g. warranty) green, every other ledger amber.
 */
export function ledgerPillClass(variant: string): string {
  return variant === 'default'
    ? 'border-[0.5px] border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
    : 'border-[0.5px] border-[var(--status-warn)]/20 bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
}

/** Badge variant for a ledger_type value, from option metadata. */
export function ledgerBadgeVariant(
  ledgerType: string,
  options?: SettlementOptions,
): BadgeVariant {
  const opt = find(options?.ledger_types, ledgerType)
  return (opt?.badge_variant as BadgeVariant | undefined) ?? 'default'
}

/**
 * Return the payment-type options that are valid for a given item_detail,
 * driven by per-option ``allowed_items`` metadata.
 *
 * Replaces the legacy ``PAYMENT_TYPE_ALLOWED_ITEMS`` Set<string> map.
 */
export function getAllowedPaymentTypes(
  itemDetail: string,
  options?: SettlementOptions,
): SettlementOptionItem[] {
  const paymentTypes = options?.payment_types ?? EMPTY
  if (!itemDetail) return paymentTypes
  return paymentTypes.filter((pt) => {
    const allowed = pt.allowed_items as string[] | undefined
    if (!Array.isArray(allowed)) return true
    return allowed.includes(itemDetail)
  })
}

/**
 * The extra Add Line Item fields that apply to an Item Detail (WPS-175 §7).
 *
 * The mapping is authored per option as ``dialog_fields`` - a list of field
 * names on top of the core capture fields every line needs. Returns null when
 * the tenant has authored no mapping, which the form reads as "fall back to
 * the older kind-driven behaviour" so an unconfigured tenant is unaffected.
 */
export function dialogFieldsForItem(
  itemDetail: string,
  options?: SettlementOptions,
): Set<string> | null {
  const opt = find(options?.item_details, itemDetail)
  const fields = opt?.dialog_fields
  if (!Array.isArray(fields)) return null
  return new Set(fields.filter((f): f is string => typeof f === 'string'))
}

/**
 * True when any Item Detail carries a ``dialog_fields`` mapping - i.e. the
 * tenant has authored the applicable-field table at all. Lets the form choose
 * between the mapping and the legacy kind-driven visibility in one place.
 */
export function hasDialogFieldMapping(options?: SettlementOptions): boolean {
  return (options?.item_details ?? EMPTY).some((o) =>
    Array.isArray(o.dialog_fields),
  )
}

/** SAP payee name limit - pure client UI hint. */
export const SAP_PAYEE_MAX_LENGTH = 34

// Options that predate the ``prorated`` flag keep this value paid in full,
// mirroring the server's LEGACY_UNPRORATED_ITEM_DETAILS.
const LEGACY_UNPRORATED_ITEM_DETAILS: ReadonlySet<string> = new Set([
  'NON_OC_MATERIAL',
])

/**
 * Item Detail values whose MATERIAL lines are paid in full, not prorated
 * (WPS-165 #12): the tenant's options declaring ``prorated: false``.
 */
export function getUnproratedItemDetails(
  options?: SettlementOptions,
): ReadonlySet<string> {
  const items = options?.item_details ?? EMPTY
  if (!items.some((o) => 'prorated' in o)) return LEGACY_UNPRORATED_ITEM_DETAILS
  return new Set(
    items
      .filter((o) => o.prorated === false)
      .map((o) => String(o.value).toUpperCase()),
  )
}
